(() => {
  if (window.blackFridayOffers) return;

  const VIP = 'bf2026_vip';
  const EXPIRY = 'bf2026_vip_expires_at';
  const GIFT = '_bf2026_gift';
  const SESSION_KEY = 'black-friday-offers:2026';
  const ENTRY_KEY = `${SESSION_KEY}:entry`;

  class BlackFridayOffers {
    constructor(config) {
      this.config = config;
      this.root = `${config.root || '/'}`.replace(/\/?$/, '/');
      this.mayActivate = config.isEntry;
      this.storageAvailable = true;
      this.session = this.readSession();
      this.entry = this.readEntry();
      this.failedSignature = '';
      this.state = { visible: false, spend: 0, status: 'progress' };
      this.observer = new MutationObserver(records => {
        if (records.some(record => {
          const target = record.target.nodeType === 1 ? record.target : record.target.parentElement;
          if (target?.closest('[data-bf-offers]')) return false;
          return record.type === 'attributes'
            ? target?.matches('cart-drawer') && target.classList.contains('active')
            : Boolean(target?.closest('#CartDrawer, cart-items, #main-cart-footer, #cart-icon-bubble'));
        })) {
          if (records.some(record => record.type === 'childList'
            && !record.target.parentElement?.closest('[data-bf-offers]'))) this.needsRender = true;
          this.schedule();
        }
      });
      this.observe();
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.cartUpdate, event => {
          if (event?.source !== 'black-friday-offers') {
            this.needsRender = true;
            this.schedule(0);
          }
        });
      }
      document.addEventListener('black-friday-offers:cart-changed', () => this.schedule(0));
      document.addEventListener('click', event => {
        if (event.target.closest('[data-bf-retry]')) {
          this.failedSignature = '';
          this.refresh();
        }
      });
      window.addEventListener('focus', () => this.schedule());
      window.addEventListener('pageshow', () => this.schedule());
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) this.schedule();
      });
      document.addEventListener('click', event => this.checkCheckout(event), true);
      document.addEventListener('submit', event => this.checkCheckout(event), true);
      // A live tab can vouch for its session without persisting anything in localStorage.
      if (typeof BroadcastChannel === 'function') {
        this.channel = new BroadcastChannel(SESSION_KEY);
        this.channel.onmessage = ({ data }) => {
          if (data?.type === 'probe' && this.validSession(data.cart, data.expiry)) {
            this.channel.postMessage({ type: 'active', request: data.request });
          }
          if (data?.type === 'active') this.peerReplies?.add(data.request);
        };
      }
      this.refresh();
    }

    readSession() {
      try {
        const raw = window.sessionStorage.getItem(SESSION_KEY);
        // Do not grant attribution if storage cannot remember completion.
        window.sessionStorage.setItem(`${SESSION_KEY}:check`, '1');
        window.sessionStorage.removeItem(`${SESSION_KEY}:check`);
        return raw ? JSON.parse(raw) : null;
      } catch (_) {
        this.storageAvailable = false;
        return null;
      }
    }

    saveSession(record) {
      try {
        window.sessionStorage.setItem(SESSION_KEY, JSON.stringify(record));
        this.session = record;
        return true;
      } catch (_) {
        this.storageAvailable = false;
        return false;
      }
    }

    readEntry() {
      try {
        const entry = JSON.parse(window.sessionStorage.getItem(ENTRY_KEY) || 'null');
        return entry?.collection === this.config.collectionId && Number.isFinite(entry.expiresAt)
          && entry.expiresAt > Date.now() ? entry : null;
      } catch (_) { return null; }
    }

    saveEntry(entry) {
      try {
        window.sessionStorage.setItem(ENTRY_KEY, JSON.stringify(entry));
        this.entry = entry;
        return true;
      } catch (_) { return false; }
    }

    clearEntry() {
      this.entry = null;
      try { window.sessionStorage.removeItem(ENTRY_KEY); } catch (_) { /* Storage can be revoked. */ }
    }

    cartIdentity(cart) {
      // Shopify can append a key to the token. Keep the stable cart identifier only.
      return String(cart.token || '').split('?')[0];
    }

    campaignActive() {
      const now = Date.now();
      return this.config.enabled && (!this.config.startsAt || Date.parse(this.config.startsAt) <= now)
        && (!this.config.endsAt || Date.parse(this.config.endsAt) > now);
    }

    expiry(cart) {
      const expiry = Number(cart.attributes?.[EXPIRY]);
      return this.config.endsAt ? Math.min(expiry, Date.parse(this.config.endsAt)) : expiry;
    }

    validSession(cartId, expiry) {
      return this.campaignActive() && this.storageAvailable && this.session?.complete === true
        && this.session.cart === cartId && this.session.collection === this.config.collectionId
        && this.session.expiresAt === Number(expiry) && Number(expiry) > Date.now();
    }

    isGift(item) {
      // Direct cart adds can omit the campaign property. Treat a free copy of
      // the configured gift as promotional too, while preserving paid copies.
      return String(item.properties?.[GIFT]) === 'true'
        || (Number(this.config.variantId) > 0 && Number(item.variant_id) === Number(this.config.variantId)
          && Number(item.final_line_price) === 0);
    }

    gifts(cart) {
      return cart.items.filter(item => this.isGift(item));
    }

    spend(cart) {
      // total_price includes cart-level discounts, which a sum of line subtotals can miss.
      const excluded = cart.items.reduce((sum, item) => sum + (this.isGift(item)
        ? Number(item.final_line_price) || 0 : 0), 0);
      return Math.max(0, Number(cart.total_price) - excluded);
    }

    pricing(cart) {
      const base = this.config.currency || 'AUD';
      const currency = cart?.currency || base;
      const storefront = window.Shopify?.currency;
      // Shopify's storefront rate converts shop currency to presentment currency.
      // Never substitute a rate for another market or compare foreign totals to AUD.
      const rate = currency === base ? 1
        : (this.config.shopCurrency || base) === base && storefront?.active === currency
          ? Number(storefront.rate) : NaN;
      const amount = Number(this.config.threshold) * rate;
      if (!/^[A-Z]{3}$/.test(currency) || !Number.isFinite(rate) || rate <= 0
        || !Number.isFinite(amount) || amount <= 0) return null;
      // Shopify encodes JPY/KRW with two trailing places too. Round the threshold
      // up to a payable whole unit for currencies without fractional units.
      const digits = new Intl.NumberFormat('en', { style: 'currency', currency }).resolvedOptions().maximumFractionDigits;
      const step = digits === 0 ? 100 : 1;
      const threshold = Math.ceil(Number((amount / step).toFixed(8))) * step;
      return Number.isSafeInteger(threshold) && threshold > 0 ? { currency, threshold } : null;
    }

    signature(cart) {
      return JSON.stringify([cart.currency, this.pricing(cart)?.threshold, cart.items.filter(item => !this.isGift(item))
        .map(item => [item.key, item.quantity, item.final_line_price])]);
    }

    async request(endpoint, data) {
      const response = await fetch(`${this.root}cart${endpoint}`, {
        method: data ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        ...(data ? { body: JSON.stringify(data) } : {})
      });
      const result = await response.json();
      if (!response.ok || result.errors || result.status >= 400) throw new Error('Cart request failed');
      return result;
    }

    sections() {
      const sections = ['cart-drawer', 'cart-icon-bubble'];
      for (const id of ['main-cart-items', 'main-cart-footer']) {
        const section = document.getElementById(id)?.dataset.id;
        if (section) sections.push(section);
      }
      return [...new Set(sections)];
    }

    async mutate(endpoint, data) {
      const result = await this.request(endpoint, {
        ...data, sections: this.sections(), sections_url: window.location.pathname
      });
      this.changed = true;
      this.needsRender = true;
      this.sectionHTML = result.sections;
      // change/update already return the authoritative cart; add returns only added lines.
      if (endpoint !== '/add.js' && Array.isArray(result.items) && typeof result.token === 'string'
        && typeof result.currency === 'string' && Number.isFinite(result.total_price) && Number.isFinite(result.item_count)) return result;
      return this.request('.js');
    }

    async otherTabActive(cart) {
      // Without a peer channel, preserve an unexpired shared cart until its fixed expiry.
      // The current tab still cannot grant itself eligibility away from the collection.
      if (!this.channel) return !this.session && this.expiry(cart) > Date.now();
      const request = `${Date.now()}-${Math.random()}`;
      this.peerReplies = this.peerReplies || new Set();
      this.channel.postMessage({ type: 'probe', request, cart: this.cartIdentity(cart), expiry: Number(cart.attributes?.[EXPIRY]) });
      await new Promise(resolve => setTimeout(resolve, 120));
      const active = this.peerReplies.has(request);
      this.peerReplies.delete(request);
      return active;
    }

    async attribute(cart) {
      const id = this.cartIdentity(cart);
      const rawExpiry = Number(cart.attributes?.[EXPIRY]);
      if (!this.storageAvailable || !id) return cart;
      const matchingRecord = this.session?.cart === id && this.session.collection === this.config.collectionId;
      const activeCart = cart.attributes?.[VIP] === 'true' && this.expiry(cart) > Date.now();
      const pending = this.entry?.collection === this.config.collectionId && this.entry.expiresAt > Date.now();
      // Resume a recorded VIP visit, bound to this cart or its exact activation
      // if Shopify changed the provisional token before navigation cancelled the response.
      const resumable = pending && (!this.entry.cart || this.entry.cart === id
        || (activeCart && rawExpiry === this.entry.expiresAt));
      if ((this.config.global || this.mayActivate || resumable) && this.campaignActive() && this.config.collectionId) {
        if (matchingRecord && this.session.complete) {
          this.mayActivate = false; // An expired record is a tombstone, not a renewal trigger.
          this.clearEntry();
        } else if (activeCart) {
          if (this.saveSession({ cart: id, collection: this.config.collectionId, complete: true, expiresAt: rawExpiry })) this.clearEntry();
          this.mayActivate = false;
        } else {
          const hours = Math.max(1, Math.min(24, Number(this.config.sessionHours) || 24));
          const expiresAt = Math.min(resumable ? this.entry.expiresAt : Date.now() + hours * 3600000,
            this.config.endsAt ? Date.parse(this.config.endsAt) : Infinity);
          if (!this.saveEntry({ cart: id, collection: this.config.collectionId, expiresAt })) return cart;
          cart = await this.mutate('/update.js', { attributes: { [VIP]: 'true', [EXPIRY]: String(expiresAt) } });
          this.mayActivate = false;
          // Shopify swaps a provisional cart token for a permanent one on the first
          // mutation, so the id read before this write is already stale. Bind the
          // record to the cart we just wrote to, or validSession() can never match
          // again and reconcile() strips the attributes it just set.
          if (!this.saveSession({ cart: this.cartIdentity(cart), collection: this.config.collectionId, complete: true, expiresAt })) {
            cart = await this.mutate('/update.js', { attributes: { [VIP]: '', [EXPIRY]: '' } });
          } else this.clearEntry();
        }
      }
      return cart;
    }

    async removeGifts(cart, keepOne = false) {
      // Use current line keys, never indexes or a bare variant ID shared with paid lines.
      for (let attempt = 0; attempt < 10; attempt++) {
        const gifts = this.gifts(cart);
        const desired = keepOne && gifts.find(line => Number(line.variant_id) === Number(this.config.variantId));
        const item = gifts.find(line => line !== desired) || (desired?.quantity !== 1 ? desired : null);
        if (!item) return cart;
        const quantity = item === desired ? 1 : 0;
        this.setBusy(quantity ? 'checking' : 'removing');
        cart = await this.mutate('/change.js', { id: item.key, quantity });
      }
      throw new Error('Gift reconciliation did not settle');
    }

    async reconcile() {
      this.changed = false;
      this.sectionHTML = null;
      let cart = await this.request('.js');
      cart = await this.attribute(cart);
      const cartId = this.cartIdentity(cart);
      const valid = cart.attributes?.[VIP] === 'true' && this.expiry(cart) > Date.now()
        && this.validSession(cartId, cart.attributes?.[EXPIRY]);
      const quote = this.pricing(cart);
      const threshold = quote?.threshold ?? Infinity;
      const peer = !valid && this.campaignActive()
        && this.expiry(cart) > Date.now() && await this.otherTabActive(cart);
      const spend = this.spend(cart);
      this.state = { visible: valid && cart.items.length > 0, spend, status: valid && !quote ? 'unavailable' : 'progress' };
      if (!peer) {
        const eligible = valid && quote && this.config.available
          && Number(this.config.variantId) > 0 && spend >= threshold;
        if (!eligible) cart = await this.removeGifts(cart);
        if (!valid && (cart.attributes?.[VIP] || cart.attributes?.[EXPIRY])) {
          cart = await this.mutate('/update.js', { attributes: { [VIP]: '', [EXPIRY]: '' } });
        }
        if (valid && spend >= threshold) {
          if (!eligible || this.failedSignature === this.signature(cart)) {
            this.state.status = 'unavailable';
          } else {
            cart = await this.removeGifts(cart, true);
            if (!this.gifts(cart).length) {
              this.state.status = 'pending';
              this.setBusy('pending');
              try {
                cart = await this.mutate('/add.js', { items: [{ id: Number(this.config.variantId), quantity: 1, properties: { [GIFT]: 'true' } }] });
              } catch (_) {
                // Stock can change after Liquid was rendered. Verify an uncertain add,
                // then let a safe paid-only cart check out instead of retrying forever.
                cart = await this.request('.js');
                if (!this.gifts(cart).length) this.failedSignature = this.signature(cart);
              }
            }
            const gifts = this.gifts(cart);
            const finalQuote = this.pricing(cart);
            if (gifts.length !== 1 || gifts[0].quantity !== 1
              || !finalQuote || this.spend(cart) < finalQuote.threshold || this.expiry(cart) <= Date.now() || !this.campaignActive()) {
              // Pricing is owned by Shopify; keep the gift at its current price while eligible.
              cart = await this.removeGifts(cart);
              this.failedSignature = this.signature(cart);
              this.state.status = 'unavailable';
            } else this.state.status = 'success';
          }
        }
      }
      this.cart = cart;
      this.quote = this.pricing(cart);
      this.state.visible = valid && cart.items.length > 0;
      this.state.spend = this.spend(cart);
      if (this.changed || this.needsRender) {
        await this.render(cart);
        this.needsRender = false;
      }
      this.paint();
      clearTimeout(this.expiryTimer);
      const next = [this.expiry(cart), Date.parse(this.config.startsAt), Date.parse(this.config.endsAt)]
        .filter(value => Number.isFinite(value) && value > Date.now());
      if (next.length) this.expiryTimer = setTimeout(() => {
        this.needsRender = true;
        this.refresh();
      }, Math.min(2147483647, Math.min(...next) - Date.now() + 20));
      document.dispatchEvent(new CustomEvent('black-friday-offers:updated', { detail: { cart } }));
    }

    observe() {
      this.observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
    }

    async render(cart) {
      let html = this.sectionHTML || {};
      if (this.sections().some(id => !html[id])) {
        const url = new URL(window.location.href);
        url.searchParams.set('sections', this.sections().join(','));
        const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store' });
        if (!response.ok) throw new Error('Cart sections failed');
        html = await response.json();
      }
      const descriptors = [
        { section: 'cart-drawer', selector: '#CartDrawer .drawer__inner' },
        { section: 'cart-icon-bubble', selector: '#cart-icon-bubble', sourceSelector: '.shopify-section' },
        ...['main-cart-items', 'main-cart-footer'].map(id => ({ section: document.getElementById(id)?.dataset.id, selector: `#${id} .js-contents` }))
      ];
      const focus = document.activeElement;
      const focusId = focus?.id;
      this.observer.disconnect();
      try {
        for (const { section, selector, sourceSelector } of descriptors) {
          const target = document.querySelector(selector);
          if (!target || !section) continue;
          const parsed = typeof html[section] === 'string' ? new DOMParser().parseFromString(html[section], 'text/html') : null;
          const source = parsed?.querySelector(sourceSelector || selector);
          if (!source) throw new Error('Cart section missing');
          target.innerHTML = source.innerHTML;
        }
        document.querySelectorAll('cart-drawer, cart-items, #main-cart-footer').forEach(element => element.classList.toggle('is-empty', !cart.item_count));
        if (focus && !focus.isConnected) {
          const replacement = focusId && document.getElementById(focusId);
          (replacement || document.querySelector('cart-drawer.active .drawer__close'))?.focus({ preventScroll: true });
        }
      } finally { this.observe(); }
    }

    setBusy(status = 'checking') {
      this.busy = true;
      this.loadingStatus = status;
      this.paint();
    }

    paint() {
      const quote = this.quote === undefined ? this.pricing(this.cart) : this.quote;
      const amount = quote ? new Intl.NumberFormat(this.config.locale || 'en-AU', { style: 'currency', currency: quote.currency })
        .format(Math.max(0, quote.threshold - this.state.spend) / 100) : '';
      document.querySelectorAll('[data-bf-offers]').forEach(element => {
        const emptyContainer = element.closest('cart-drawer.is-empty, cart-items.is-empty');
        const container = element.closest('cart-drawer, cart-items');
        const hasRows = Boolean(container?.querySelector('.cart-item'));
        const candidate = this.campaignActive() && this.storageAvailable && (this.mayActivate
          || (this.session?.complete && this.session.collection === this.config.collectionId && this.session.expiresAt > Date.now()));
        const loadingVisible = this.busy && hasRows && (candidate || container?.querySelector('.cart-item--mystery-gift'));
        element.hidden = Boolean(emptyContainer) || !(loadingVisible || (this.state.visible && this.cart?.items.length !== 0));
        const activeStatus = this.busy ? this.loadingStatus : this.state.status;
        const headline = element.querySelector('[data-bf-headline]');
        const value = element.querySelector('[data-bf-value]');
        if (value) value.hidden = !this.cart || this.gifts(this.cart).length > 0;
        const status = element.querySelector('[data-bf-status]');
        const progress = element.querySelector('[data-bf-progress]');
        const text = this.config[activeStatus] || ({ checking: 'Checking your Mystery Gift…', removing: 'Removing your Mystery Gift…' })[activeStatus] || '';
        if (headline.textContent !== this.config.headline) headline.textContent = this.config.headline || '';
        const message = activeStatus === 'progress' ? text.replaceAll('[amount]', amount) : text;
        if (status.textContent !== message) status.textContent = message;
        progress.value = quote ? Math.min(100, Math.max(0, this.state.spend / quote.threshold * 100)) || 0 : 0;
        progress.hidden = Boolean(this.busy);
        element.querySelector('[data-bf-loading]').hidden = !this.busy;
        element.querySelector('[data-bf-retry]').hidden = this.busy || !['error', 'unavailable'].includes(this.state.status);
      });
    }

    schedule(delay = 80) {
      this.setBusy(); // Give feedback before the debounce or native cart request finishes.
      clearTimeout(this.debounce);
      this.debounce = setTimeout(() => this.refresh(), delay);
    }

    refresh() {
      clearTimeout(this.debounce);
      this.setBusy();
      this.dirty = true;
      if (this.job) return this.job;
      let waitingForNative = false;
      const work = async () => {
        let passes = 0;
        do {
          this.dirty = false;
          // Let native product forms/quantity controls finish their current request.
          if (document.querySelector('.cart__items--disabled, product-form button.loading, ext-custom-bundle button.loading')) {
            waitingForNative = true;
            this.schedule();
            return;
          }
          await this.reconcile();
        } while (this.dirty && ++passes < 3);
      };
      this.job = (navigator.locks ? navigator.locks.request(SESSION_KEY, work) : work())
        .then(() => { this.error = false; })
        .catch(() => {
          this.error = true;
          this.state.status = 'error';
          this.state.visible = Boolean(this.session || this.config.isEntry);
          this.paint();
        }).finally(() => {
          this.job = null;
          if (!waitingForNative) this.busy = false;
          this.paint();
        });
      return this.job;
    }

    async checkCheckout(event) {
      if (this.replaying || event.defaultPrevented) return;
      const target = event.type === 'submit' ? event.submitter : event.target.closest('button, input, a');
      const form = event.type === 'submit' ? event.target : target?.form;
      const link = target?.closest('a[href]');
      const checkoutLink = link && new URL(link.href, window.location.href);
      const checkout = target?.name === 'checkout' || (checkoutLink && checkoutLink.origin === window.location.origin && /\/checkout(?:\/|$)/.test(checkoutLink.pathname));
      if (!checkout) return; // Do not interfere with product forms or ordinary cart updates.
      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.checkingCheckout) return;
      this.checkingCheckout = true;
      try {
        await this.refresh();
        if (this.error || this.dirty || document.querySelector('.cart__items--disabled, product-form button.loading, ext-custom-bundle button.loading')) return;
        const cart = await this.request('.js');
        const sessionActive = this.validSession(this.cartIdentity(cart), cart.attributes?.[EXPIRY])
          || (this.campaignActive() && cart.attributes?.[VIP] === 'true' && this.expiry(cart) > Date.now() && await this.otherTabActive(cart));
        // If eligibility cleanup failed, reconcile before continuing to checkout.
        const quote = this.pricing(cart);
        if (this.gifts(cart).length && (!sessionActive || !quote
          || this.expiry(cart) <= Date.now() || this.spend(cart) < quote.threshold)) {
          throw new Error('Gift cart requires another check');
        }
        this.replaying = true;
        if (checkoutLink) window.location.assign(checkoutLink.href);
        else if (event.type === 'submit') form.requestSubmit(target);
        else if (target.isConnected) target.click();
        else (document.querySelector('#CartDrawer-Checkout') || document.querySelector('#checkout'))?.click();
      } catch (_) {
        this.state = { ...this.state, visible: true, status: 'error' };
        this.paint();
      } finally {
        this.replaying = false;
        this.checkingCheckout = false;
      }
    }
  }

  const configElement = document.getElementById('BlackFridayOffersConfig');
  if (!configElement) return;
  try {
    window.blackFridayOffers = new BlackFridayOffers(JSON.parse(configElement.textContent));
  } catch (_) {
    // Bad merchant configuration must not break the rest of the theme.
    console.warn('Black Friday Offers could not initialize. Check the campaign settings.');
  }
})();
